<template>
  <button
    v-bind="{ ...$props, style: {} }"
    class="flex items-center justify-center rounded-full transition duration-100 ease-linear"
    :class="[customStyles, { 'm-2': !noMargin }]"
    :aria-label="ariaLabel"
    :title="title"
    @click="onClick"
  >
    <div
      :key="selected ? 'circle-icon-active' : 'circle-icon-inactive'"
      class="flex flex-col justify-center"
    >
      <slot name="icon" />
    </div>
  </button>
</template>

<script setup lang="ts">
const props = defineProps({
  size: {
    type: Number as PropType<number>,
    validator: (value: number) => {
      return value > 5 && value < 13
    },
    default: undefined,
  },
  backgroundColorClass: {
    type: String as PropType<string>,
    default: 'bg-black',
  },
  backgroundColorHoverClass: {
    type: String as PropType<string>,
    default: 'hover:bg-gray-500',
  },
  selected: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String as PropType<string>,
    default: undefined,
  },
  title: {
    type: String as PropType<string>,
    required: true,
  },
  noMargin: {
    type: Boolean,
    default: false,
  },
  /**
   * The border color as Tailwind utility color
   * @values black, [#000000]
   */
  borderColor: {
    type: String as PropType<string>,
    default: undefined,
  },
  /**
   * The hover border color as Tailwind utility color
   * @values black, [#000000]
   */
  borderColorHover: {
    type: String as PropType<string>,
    default: undefined,
  },
})

const emit = defineEmits(['click'])

const onClick = () => {
  emit('click', { selected: !props.selected })
}

const customStyles = computed(() => {
  const size = props.size
    ? [`h-${props.size}`, `w-${props.size}`]
    : ['h-10', 'w-10', 'md:h-12', 'md:w-12']

  const background =
    props.backgroundColorClass && props.backgroundColorHoverClass
      ? [`${props.backgroundColorClass}`, `${props.backgroundColorHoverClass}`]
      : ['bg-black', 'hover:bg-gray-500']

  const border = props.borderColor
    ? [
        `border border-${props.borderColor} hover:border-${props.borderColorHover}`,
      ]
    : []

  return [...size, ...background, ...border]
})
</script>
